import React from "react";
import { createRoot } from "react-dom/client";
import { Routes, Route, HashRouter } from "react-router-dom";

import { Coach } from "./routes/coach";
import { League } from "./routes/league";
import { Leagues } from "./routes/leagues";
import { Login } from "./routes/login";
import { Logout } from "./routes/logout";
import { Seasons } from "./routes/seasons";
import { Season } from "./routes/season";
import { ErrorBoundary } from "./components/ErrorBoundary";

import { Amplify } from 'aws-amplify';

// https://docs.amplify.aws/lib/auth/start/q/platform/js/#re-use-existing-authentication-resource

// Determine the current environment and set appropriate redirect URLs, cookie domain, and OAuth domain
const getAuthConfig = () => {
  // Check if we're in a browser environment
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port;
    
    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return {
        redirectSignIn: `${protocol}//${hostname}:${port || '4000'}`,
        redirectSignOut: `${protocol}//${hostname}:${port || '4000'}`,
        cookieDomain: 'localhost',
        oauthDomain: 'jhoytio-blood-bowl-beta.auth.us-east-1.amazoncognito.com',
        userPoolId: 'us-east-1_8wLKsqn3T',
        userPoolWebClientId: '6irenarjp2f0ofuin80i5dptrf'
      };
    }
    
    // Beta environment
    if (hostname === 'beta.blood-bowl.jhoyt.io') {
      return {
        redirectSignIn: 'https://beta.blood-bowl.jhoyt.io/',
        redirectSignOut: 'https://beta.blood-bowl.jhoyt.io/',
        cookieDomain: '.jhoyt.io',
        oauthDomain: 'jhoytio-blood-bowl-beta.auth.us-east-1.amazoncognito.com',
        userPoolId: 'us-east-1_8wLKsqn3T',
        userPoolWebClientId: '6irenarjp2f0ofuin80i5dptrf'
      };
    }
    
    // Production environment
    if (hostname === 'blood-bowl.jhoyt.io') {
      return {
        redirectSignIn: 'https://blood-bowl.jhoyt.io/',
        redirectSignOut: 'https://blood-bowl.jhoyt.io/',
        cookieDomain: '.jhoyt.io',
        oauthDomain: 'jhoytio-blood-bowl-prod.auth.us-east-1.amazoncognito.com',
        userPoolId: 'us-east-1_g464qoGXq',
        userPoolWebClientId: '1b254rv326c45cmfspnr6erqtm'
      };
    }
  }
  
  // Fallback for server-side rendering or unknown environments
  return {
    redirectSignIn: 'http://localhost:4000',
    redirectSignOut: 'http://localhost:4000',
    cookieDomain: 'localhost',
    oauthDomain: 'jhoytio-blood-bowl-beta.auth.us-east-1.amazoncognito.com',
    userPoolId: 'us-east-1_8wLKsqn3Tj',
    userPoolWebClientId: '6irenarjp2f0ofuin80i5dptrf'
  };
};

const authConfig = getAuthConfig();

Amplify.configure({
  Auth: {
    Cognito: {
      // REQUIRED - Amazon Cognito User Pool ID
      userPoolId: authConfig.userPoolId,

      // REQUIRED - Amazon Cognito Web Client ID (26-char alphanumeric string)
      userPoolClientId: authConfig.userPoolWebClientId,

      // OAuth configuration for hosted UI
      loginWith: {
        oauth: {
          domain: authConfig.oauthDomain,
          scopes: ['email', 'openid', 'profile'],
          redirectSignIn: [authConfig.redirectSignIn],
          redirectSignOut: [authConfig.redirectSignOut],
          responseType: 'code'
        }
      }
    }
  }
});

const container = document.getElementById("root");
if (!container) throw new Error('Failed to find the root element');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <HashRouter>
        <Routes>
          <Route path="/" element={<Leagues />} />
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/coach" element={<Coach />} />
          <Route path="/coach/:id" element={<Coach />} />
          <Route path="/leagues" element={<Leagues />} />
          <Route path="/leagues/:id" element={<League />} />
          <Route path="/seasons" element={<Seasons />} />
          <Route path="/seasons/:id" element={<Season />} />
        </Routes>
      </HashRouter>
    </ErrorBoundary>
  </React.StrictMode>
);